import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { environment } from 'src/environments/environment';
import { SafeStorage } from '../utils/safe-storage';
import { AnalyticsService } from './analytics.service';

export type AdsConversionType = 'signup' | 'event_created';

/** Meta Pixel event name per conversion. Must match the Lambda's CONVERSIONS entries. */
const META_EVENT: Record<AdsConversionType, { method: 'track' | 'trackCustom'; name: string }> = {
  signup: { method: 'track', name: 'CompleteRegistration' },
  event_created: { method: 'trackCustom', name: 'EventCreated' },
};

/**
 * Remembers the signup's id until the server has settled it. In the create-first
 * flow the account only exists once pwa_create_event has run, so the signup
 * report can miss it; the event_created report then carries this id and the
 * Lambda records the signup too, matched to the same browser Pixel event.
 */
const PENDING_SIGNUP_KEY = 'ads_pending_signup_event_id';

/** Seconds to wait before asking again when the account does not exist yet. */
const ACCOUNT_RETRY_DELAYS = [5, 15, 30];

/**
 * Reports ad conversions twice, with one shared id:
 *   - the browser Meta Pixel, straight away;
 *   - the ads-conversion-tracking Lambda, which checks the database, records
 *     the conversion once and forwards it to Meta (Conversions API) and Google.
 * Meta merges the two because they share the event id.
 *
 * Fire-and-forget: never throws and never delays the screen that calls it.
 */
@Injectable({ providedIn: 'root' })
export class AdsConversionService {

  constructor(private afAuth: AngularFireAuth, private analytics: AnalyticsService) { }

  track(type: AdsConversionType, options: { eventId?: string | number } = {}): void {
    const clientEventId = newUuid();
    const pixel = META_EVENT[type];
    try {
      (window as any).fbq?.(pixel.method, pixel.name, {}, { eventID: clientEventId });
    } catch {
      // The Pixel is optional; the server report below still goes out.
    }
    if (type === 'signup') SafeStorage.setItem(PENDING_SIGNUP_KEY, clientEventId);

    void this.report(type, clientEventId, options.eventId, 0);
  }

  private async report(type: AdsConversionType, clientEventId: string, eventId: string | number | undefined, attempt: number): Promise<void> {
    try {
      const user = await this.afAuth.currentUser;
      if (!user) return;
      const token = await user.getIdToken();

      const pendingSignup = SafeStorage.getItem(PENDING_SIGNUP_KEY);
      const body = {
        type,
        platform: 'pwa',
        client_event_id: clientEventId,
        event_id: eventId != null ? String(eventId) : undefined,
        also_client_event_ids: type !== 'signup' && pendingSignup ? { signup: pendingSignup } : undefined,
        page_url: window.location.href,
        click: {
          fbc: readCookie('_fbc'),
          fbp: readCookie('_fbp'),
          gclid: SafeStorage.getItem('gclid'),
          gbraid: SafeStorage.getItem('gbraid'),
          wbraid: SafeStorage.getItem('wbraid'),
        },
        utm: {
          source: SafeStorage.getItem('utm_source'),
          medium: SafeStorage.getItem('utm_medium'),
          campaign: SafeStorage.getItem('utm_campaign'),
          content: SafeStorage.getItem('utm_content'),
          term: SafeStorage.getItem('utm_term'),
        },
      };

      const response = await post(environment.adsConversionTrackingUrl, token, body);
      const result = await response.json().catch(() => null);

      if (result?.reason === 'account_not_found' && attempt < ACCOUNT_RETRY_DELAYS.length) {
        setTimeout(() => void this.report(type, clientEventId, eventId, attempt + 1), ACCOUNT_RETRY_DELAYS[attempt] * 1000);
        return;
      }
      if (!response.ok || !result?.ok) {
        this.analytics.capture('ads_conversion_report_failed', { type, status: response.status, reason: result?.reason });
        return;
      }

      // Once the server has decided about the signup (recorded, duplicate or not
      // new), stop carrying it on later reports.
      if ((result.results || []).some((r: any) => r.type === 'signup')) {
        SafeStorage.removeItem(PENDING_SIGNUP_KEY);
      }

      // New accounts get no analytics_id at sign-in (the account does not exist
      // yet), so this is the first point the PostHog person can be linked to it.
      const analyticsId = result.analytics_id;
      if (analyticsId && SafeStorage.getItem('analytics_id') !== analyticsId) {
        SafeStorage.setItem('analytics_id', analyticsId);
        this.analytics.identify(analyticsId);
      }
    } catch (error: any) {
      this.analytics.capture('ads_conversion_report_failed', { type, reason: error?.message || 'unknown' });
    }
  }
}

/**
 * keepalive lets the request finish if the page is closed or navigates away
 * straight after the conversion. Browsers that refuse keepalive for a request
 * needing a CORS preflight throw a TypeError; retry as an ordinary request.
 */
async function post(url: string, token: string, body: unknown): Promise<Response> {
  const init: RequestInit = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body: JSON.stringify(body),
  };
  try {
    return await fetch(url, { ...init, keepalive: true });
  } catch (error) {
    if (error instanceof TypeError) return fetch(url, init);
    throw error;
  }
}

function readCookie(name: string): string | null {
  try {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

function newUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
